<template>
  <CCard>
    <!-- Header -->
    <CCardHeader>
      <div
        class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3"
      >
        <h2 class="text-uppercase font-weight-bold mb-2 mb-md-0">Gyms</h2>

        <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
          <CButton color="primary" class="py-2 px-3 w-100 mb-2" @click="addGym">
            Add New Gym
          </CButton>

          <CInput
            v-model="filters.search"
            placeholder="Search"
            class="py-md-2 px-md-3 w-100 mb-2"
            @update:value="fetchData"
          />

          <label class="d-flex align-items-center mb-2">
            <input v-model="onlyActive" type="checkbox" class="mr-1" @change="toggleOnlyActive" />
            Only Active
          </label>
        </div>
      </div>
    </CCardHeader>

    <!-- Active Filter badge -->
    <CCardHeader v-if="filters.active !== null">
      Active Filter:
      <span class="badge bg-info text-white h6">{{ filters.active ? 'Yes' : 'No' }}</span>
    </CCardHeader>

    <!-- Table -->
    <CCardBody>
      <CDataTable
        :items="data"
        :fields="c_fields"
        :pagination="false"
        :sorter="{ external: true }"
        :loading="loading"
        :stacked="true"
        class="mb-0"
        @update:sorter-value="sortingChange"
      >
        <template #subscription="gym">
          <td>
            <div v-if="gym.item.current_subscription">
              <strong>{{ gym.item.current_subscription.name }}</strong>
              <br />
              Status: {{ gym.item.current_subscription.stripe_status }}
              <br />
              Ends at: {{ formatLocalDate(gym.item.current_subscription.ends_at) }}
            </div>
            <span v-else>—</span>
          </td>
        </template>

        <template #active="gym">
          <td>
            <span :class="gym.item.active ? 'badge bg-success' : 'badge bg-secondary'">
              {{ gym.item.active ? 'Yes' : 'No' }}
            </span>
          </td>
        </template>

        <template #actions="gym">
          <td>
            <div class="d-flex flex-column gap-3">
              <CButton
                v-if="gym.item.stripe_status === 'manual'"
                size="sm"
                color="warning"
                class="mb-2 text-white"
                @click="editGym(gym.item.id)"
              >
                Edit
              </CButton>
              <CButton
                v-if="gym.item.stripe_status === 'manual'"
                size="sm"
                color="danger"
                class="mb-2 text-white"
                @click="deleteSubscription(gym.item.id)"
              >
                Delete
              </CButton>
              <CButton
                v-if="!gym.item.active"
                size="sm"
                color="success"
                class="mb-2 text-white"
                @click="activateGym(gym.item.id)"
              >
                Activate
              </CButton>
              <CButton
                v-if="gym.item.active"
                size="sm"
                color="danger"
                class="mb-2 text-white"
                @click="deactivateGym(gym.item.id)"
              >
                Deactivate
              </CButton>
              <CButton
                size="sm"
                color="info"
                class="mb-2 text-white"
                @click="goToSubscriptions(gym.item.id)"
              >
                Subscriptions
              </CButton>
              <CButton
                size="sm"
                color="secondary"
                class="mb-2 text-white"
                @click="forgotPassword(gym.item)"
              >
                Forgot Password
              </CButton>
            </div>
          </td>
        </template>
      </CDataTable>

      <pagination
        :current-page="options.page"
        :last-page="options.last_page"
        class="mr-3"
        @paginate="pageChange"
      />
    </CCardBody>
  </CCard>
</template>

<script>
import Pagination from '@/components/Pagination';

export default {
  name: 'Gyms',
  components: { Pagination },
  data() {
    return {
      filters: { search: '', active: null },
      onlyActive: false,
      data: [],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'active', label: 'Active', sorter: false },
        { key: 'subscription', label: 'Subscription', sorter: false },
      ],
      url: '/gym',
      options: {
        page: 1,
        last_page: 1,
        total: 0,
        sorter: { column: 'id', direction: 'desc' },
      },
      loading: false,
    };
  },
  computed: {
    c_fields() {
      let fields = this.fields.slice();
      fields.push({ key: 'actions', label: 'Actions', sorter: false });
      return fields;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    buildParams() {
      const params = {
        page: this.options.page,
        sort_by: this.options.sorter.column,
        sort_order: this.options.sorter.direction,
      };
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.active) params.active = 1;
      return params;
    },
    fetchData() {
      this.loading = true;
      this.$http
        .get(this.url, { params: this.buildParams() })
        .then(({ data }) => {
          this.data = data.data;
          this.options.page = data.current_page;
          this.options.last_page = data.last_page;
          this.options.total = data.total;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    pageChange(page) {
      this.options.page = page;
      this.fetchData();
    },
    sortingChange(sorter) {
      const column = sorter.column || sorter.key;
      let direction = 'asc';
      if (this.options.sorter.column === column) {
        direction = this.options.sorter.direction === 'asc' ? 'desc' : 'asc';
      }

      this.options.sorter = { column, direction };
      this.fetchData();
    },
    formatLocalDate(dateString) {
      if (!dateString) return '—';

      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');

      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    },
    toggleOnlyActive() {
      this.filters.active = this.onlyActive ? true : null;
      this.options.page = 1;
      this.fetchData();
    },
    addGym() {
      this.$router.push('/gyms/new');
    },
    editGym(id) {
      this.$router.push(`/gyms/${id}/edit`);
    },
    activateGym(id) {
      this.$http.put(`/gym/${id}`, { active: 1 }).then(() => {
        this.$toast.success('Gym activated!');
        this.fetchData();
      });
    },
    deactivateGym(id) {
      this.$http.put(`/gym/${id}`, { active: 0 }).then(() => {
        this.$toast.success('Gym deactivated!');
        this.fetchData();
      });
    },
    goToSubscriptions(gymId) {
      this.$router.push(`/gyms/${gymId}/subscriptions`);
    },
    forgotPassword(gym) {
      this.$http
        .post(`/gym/forgot-password`, { email: gym.email })
        .then(() => {
          this.$toast.success('Password reset link sent to gym email.');
        })
        .catch((err) => {
          const msg = err.response?.data?.message || 'Error sending reset link.';
          this.$toast.error(msg);
        });
    },
  },
};
</script>

<style scoped>
.badge.bg-success {
  background-color: #28a745;
  color: white;
}
.badge.bg-secondary {
  background-color: #6c757d;
  color: white;
}
</style>
